import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * LiveCountsContext — single polling source for the operational badges
 * that appear in MULTIPLE places (Topbar bell + Sidebar nav items).
 *
 * Without this context, we'd have:
 *   - Topbar polling every 60s for awaiting_human + negative_reviews
 *   - Sidebar polling the same two endpoints every 60s
 *   - Two pollers racing for the same data, twice the load, twice the
 *     flicker, possible UI inconsistency (badge says 3 in one place
 *     and 2 in the other for a few seconds).
 *
 * This provider:
 *   1. Does ONE poll every 60s
 *   2. Both consumers read from the same state
 *   3. New consumers (e.g. an in-app push toast) can subscribe later
 *      without adding more network load
 *
 * Failures are silent — a missing badge is better than a noisy console.
 */
const LiveCountsContext = createContext({
  awaitingHumanCount: 0,
  negativeReviewsCount: 0,
  pendingQuestionsCount: 0,
  awaitingHumanItems: [],
  negativeReviewsItems: [],
  pendingQuestionsItems: [],
  refresh: () => {},
});

const POLL_MS = 60_000;
const PENDING_POLL_MS = 30_000;
const RECENT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export function LiveCountsProvider({ children, enabled = true }) {
  const [awaitingHumanCount, setAwaitingHumanCount] = useState(0);
  const [negativeReviewsCount, setNegativeReviewsCount] = useState(0);
  const [pendingQuestionsCount, setPendingQuestionsCount] = useState(0);
  const [awaitingHumanItems, setAwaitingHumanItems] = useState([]);
  const [negativeReviewsItems, setNegativeReviewsItems] = useState([]);
  const [pendingQuestionsItems, setPendingQuestionsItems] = useState([]);

  const refresh = async () => {
    try {
      const [a, neg] = await Promise.all([
        api.get("/dashboard/awaiting_human?limit=20").catch(() => ({ data: { total: 0, items: [] } })),
        api.get("/audit?action=review.negative_received&limit=20").catch(() => ({ data: [] })),
      ]);
      setAwaitingHumanCount(a.data?.total || 0);
      setAwaitingHumanItems(a.data?.items || []);

      // Defense in depth: the backend now filters by `action`, but we
      // still drop anything that doesn't match — protects against any
      // future regression where the param is silently dropped (see
      // iteration 51 hotfix).
      const cutoff = Date.now() - RECENT_WINDOW_MS;
      const recent = (neg.data || []).filter(
        (l) =>
          l?.action === "review.negative_received" &&
          new Date(l.created_at).getTime() >= cutoff,
      );
      setNegativeReviewsCount(recent.length);
      setNegativeReviewsItems(recent);
    } catch { /* swallow — badge stays at last known good */ }
  };

  // Pending questions (v0.94 — operator relay handoff) — polled at
  // 30s for a snappier badge. Notifies the browser when a new one
  // arrives (sound + system notification).
  const refreshPending = async () => {
    try {
      const r = await api.get("/pending-questions?status=waiting&limit=20").catch(() => ({ data: [] }));
      const items = r.data || [];
      setPendingQuestionsCount(items.length);
      // detect "new since last poll" — alert only for NEW items.
      const knownIds = new Set(pendingQuestionsItems.map((p) => p.id));
      const newOnes = items.filter((p) => !knownIds.has(p.id));
      if (newOnes.length > 0 && pendingQuestionsItems.length > 0) {
        // Fire a browser push + sound (best-effort; ignored silently
        // if user hasn't granted permission yet).
        notifyOperator(newOnes[0]);
      }
      setPendingQuestionsItems(items);
    } catch { /* swallow */ }
  };

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    refreshPending();
    const t1 = setInterval(refresh, POLL_MS);
    const t2 = setInterval(refreshPending, PENDING_POLL_MS);
    // Re-fetch immediately when the tab becomes visible again — avoids
    // 30s delay on badge after switching back to AgentUS (demo-critical
    // when operator alt-tabs to check WhatsApp).
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        refresh();
        refreshPending();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(t1);
      clearInterval(t2);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return (
    <LiveCountsContext.Provider
      value={{
        awaitingHumanCount,
        negativeReviewsCount,
        pendingQuestionsCount,
        awaitingHumanItems,
        negativeReviewsItems,
        pendingQuestionsItems,
        refresh,
        refreshPending,
      }}
    >
      {children}
    </LiveCountsContext.Provider>
  );
}

// ── Browser notification + sound when a new pending question lands ──
// Beep is a tiny inline WebAudio "boop" so we don't need to ship an
// audio file. Notification is opt-in (Permission API).
let _audioCtx = null;
function playBeep() {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(880, t);
    o.frequency.exponentialRampToValueAtTime(1320, t + 0.08);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
    o.connect(g).connect(ctx.destination);
    o.start(t);
    o.stop(t + 0.4);
  } catch { /* user gesture not received yet — silent */ }
}

function notifyOperator(pq) {
  // sound
  playBeep();
  // browser push
  try {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "granted") {
      const n = new Notification("Nova pergunta pendente — AgentUS", {
        body: `${pq.customer_name || "Cliente"}: ${(pq.question_text || "").slice(0, 120)}`,
        tag: `pq-${pq.id}`,
        icon: "/logo-black.png",
      });
      n.onclick = () => {
        window.focus();
        window.location.assign("/app/perguntas-pendentes");
        n.close();
      };
    }
  } catch { /* swallow */ }
}

export function useLiveCounts() {
  return useContext(LiveCountsContext);
}
